/**
 * BaseController — the only class that defines initialize/connect/disconnect.
 * Every controller extends it (directly or via FormController / PopoverController) and implements hooks:
 *
 *   setup()   synchronous wiring once `this.config` is resolved. Optional.
 *   start({signal, generation}) data work for one run. HTTP returns stale after that run is invalidated.
 *   clear()   idempotent: remove rendered records, reset to `loading`. Required for components that
 *             render API data. Called on disconnect and before a page snapshot / pagehide.
 *
 * Order on connect: new Lifetime -> this.http -> clear stale aria-busy -> resolve config
 * -> prepare() -> setup() -> `start` gate -> start().  On disconnect: clear() -> lifetime.dispose().
 *
 * `this.lifetime` (never `scope`: Stimulus owns that name), `this.signal`, `this.config` (frozen),
 * `this.http` (a client bound to the current run; setup/config use the connection, busy element = this.element).
 *
 * Config is declared with `static config = { key: { type, default, required, min, max, oneOf } }`
 * (see config.js). Invalid config fails closed: the element shows `error` (kind `config`), nothing starts.
 * A value attribute changed at runtime re-resolves the config and restarts: clear() -> start().
 */
import { Controller } from '@hotwired/stimulus'
import { configSource, schemaOf } from './config.js'
import { emit } from './events.js'
import { createHttp } from './http.js'
import { Lifetime } from './lifetime.js'
import { showState } from './render.js'
import { startWhen } from './timing.js'

const DEAD = new Lifetime()
DEAD.dispose()

export class BaseController extends Controller {
  /** @type {Lifetime | null} owned resources of the current connection */
  lifetime = null
  /** @type {Readonly<Record<string, any>> | null} */
  config = null
  /** @type {ReturnType<typeof createHttp> | null} stays usable after disconnect: every call answers `stale` */
  http = null

  #ready = false // setup() has run for this connection
  #started = false // start() has been called at least once for this connection
  #generation = 0 // bumped by every (re)boot: late async continuations of an older one are ignored
  #pending = false // a restart is waiting for restartBlocked to clear
  #cleared = false // clear() ran for a page snapshot; a bfcache restore must restart
  #disposing = false // disconnect() is running: no restart may start work again
  #refreshing = false // the ls:refresh listener of this connection exists
  /** @type {Lifetime | null} */
  #gate = null
  /** @type {ReturnType<typeof configSource> | null} */
  #source = null
  /** @type {Lifetime | null} data work of the current start(), disposed before restart/failure */
  #run = null
  /** @type {ReturnType<typeof createHttp> | null} config resolution belongs to the connection, not an old run */
  #configHttp = null

  connect() {
    if (this.lifetime) return // connect() is idempotent
    const lifetime = (this.lifetime = new Lifetime())
    this.http = this.#configHttp = createHttp(lifetime, { busy: this.element })
    this.element.removeAttribute('aria-busy') // may be left over in a restored snapshot
    this.#ready = false
    this.#started = false
    this.#pending = false
    this.#cleared = false
    this.#disposing = false
    this.#refreshing = false

    const snapshot = () => {
      if (!this.#ready) return
      this.#cleared = true
      this.#invalidateRun()
      this.clear()
    }
    lifetime.listen(document, 'turbo:before-cache', snapshot)
    lifetime.listen(window, 'pagehide', snapshot)
    lifetime.listen(window, 'pageshow', (event) => {
      if (event.persisted && this.#cleared) this.restart()
    })

    this.safeguard() // fail-closed safeguards that must hold even when the config is invalid
    this.#source = configSource(this.element, this.identifier, schemaOf(this.constructor))
    this.#source.watch(lifetime, () => this.restart({ configChange: true }))
    this.#resolve((resolved) => {
      this.config = resolved.config
      const problem = this.prepare()
      if (problem) return this.#fail(problem)
      this.#ready = true
      this.setup()
      this.#listenRefresh()
      this.#armGate()
    })
  }

  disconnect() {
    const lifetime = this.lifetime
    if (!lifetime) return
    this.#disposing = true
    try {
      this.#invalidateRun()
      if (this.#ready) this.clear()
    } finally {
      this.#generation++
      this.#ready = false
      lifetime.dispose()
      this.lifetime = null
    }
  }

  /** Hook for BASE CLASSES: runs on connect BEFORE config resolution (e.g. FormController blocks native submit). */
  safeguard() {}

  /** Hook. Synchronous wiring after config is resolved (`this.config` is set). */
  setup() {}

  /** Hook. Data work; capture the signal before awaits when using a transport other than this.http. */
  start(_run) {}

  /** Hook. Idempotent: remove rendered records and reset to `loading`. */
  clear() {}

  /**
   * Hook for BASE CLASSES (FormController, PopoverController), not for components. Runs before setup().
   * Return `{ kind, message? }` to stop the component with that error (fail closed).
   * @returns {{ kind: string, message?: string } | void}
   */
  prepare() {}

  /** Hook. While true, a config-triggered restart waits (a dirty form). Call resumeRestart() when it clears. */
  get restartBlocked() {
    return false
  }

  /** Opt in only when a valid local config update preserves the current in-flight operation. */
  equivalentConfig(_next) {
    return false
  }

  /** Run a restart that was deferred by `restartBlocked`, if it may run now. */
  resumeRestart() {
    if (this.#pending && !this.#cleared && !this.#disposing && !this.restartBlocked) this.restart()
  }

  /** Re-resolve the config and restart: clear() -> start(). Deferred while `restartBlocked`. */
  restart({ configChange = false } = {}) {
    if (!this.lifetime || this.#disposing) return
    if (this.restartBlocked) {
      this.#pending = true
      return
    }
    if (configChange && this.#ready && this.#run?.active && !this.#source.hasRemote()) {
      const next = this.#source.local()
      if (!next.errors.length && this.equivalentConfig(next.config)) {
        this.config = next.config
        return
      }
    }
    this.#pending = false
    this.#cleared = false
    this.#gate?.dispose()
    this.#invalidateRun()
    if (this.#ready) this.clear()
    this.#resolve((resolved) => {
      this.config = resolved.config
      if (!this.#ready) {
        const problem = this.prepare()
        if (problem) return this.#fail(problem)
        this.#ready = true
        this.setup()
        this.#listenRefresh()
        return this.#armGate()
      }
      this.#armGate(this.#started ? 'connect' : undefined) // already started once: no new wait
    })
  }

  /** Resolve config (local, or with the config URL), then call `next(resolved)` unless superseded. */
  #resolve(next) {
    const generation = ++this.#generation
    const lifetime = this.lifetime
    const source = this.#source
    const done = (resolved) => {
      if (generation !== this.#generation || !lifetime?.active) return
      if (resolved.stale) return
      if (resolved.errors.length) return this.#fail({ kind: 'config' })
      next(resolved)
    }
    if (source.hasRemote()) source.load(this.#configHttp).then(done)
    else done(source.local())
  }

  /** Reserved `refreshKey`: `ls:refresh` with that key in `detail.keys` restarts the component (no-op while blocked). */
  #listenRefresh() {
    if (this.#refreshing) return
    this.#refreshing = true
    this.lifetime.listen(window, 'ls:refresh', (event) => {
      const key = this.config?.refreshKey // read per event: the attribute may change at runtime
      if (!key || this.restartBlocked) return
      if (Array.isArray(event.detail?.keys) && event.detail.keys.includes(key)) this.restart()
    })
  }

  /** Fail closed: show the `error` state (kind `config`), start nothing. */
  #fail(error) {
    this.#gate?.dispose()
    this.#invalidateRun()
    this.#generation++
    showState(this.element, 'error', error)
  }

  #invalidateRun() {
    this.#run?.dispose()
    this.#run = null
  }

  /** @param {'connect' | 'visible' | 'idle' | 'interaction'} [mode] defaults to config.start */
  #armGate(mode = this.config.start) {
    const generation = this.#generation
    this.#gate?.dispose()
    this.#gate = this.lifetime.child()
    startWhen(this.#gate, this.element, mode, () => {
      if (generation !== this.#generation || !this.lifetime?.active) return
      this.#invalidateRun()
      const run = (this.#run = this.lifetime.child())
      this.http = createHttp(run, { busy: this.element })
      this.#started = true
      const report = (error) => {
        if (run.active && generation === this.#generation) this.#report(error)
      }
      try {
        Promise.resolve(this.start({ signal: run.signal, generation })).catch(report)
      } catch (error) {
        report(error)
      }
    })
  }

  #report(error) {
    this.application.handleError(error, `${this.identifier}: start() failed`, { identifier: this.identifier })
  }

  /** AbortSignal of the current connection (already aborted when disconnected). */
  get signal() {
    return (this.lifetime ?? DEAD).signal
  }

  /** @type {Lifetime['listen']} */
  listen(target, type, handler, options) {
    ;(this.lifetime ?? DEAD).listen(target, type, handler, options)
  }

  /** @type {Lifetime['timeout']} returns a cancel function; debounce = cancel + timeout */
  timeout(fn, ms) {
    return (this.lifetime ?? DEAD).timeout(fn, ms)
  }

  /** @type {Lifetime['interval']} */
  interval(fn, ms) {
    return (this.lifetime ?? DEAD).interval(fn, ms)
  }

  /** @type {Lifetime['observe']} */
  observe(observer, target, options) {
    return (this.lifetime ?? DEAD).observe(observer, target, options)
  }

  /** Register a cleanup that runs on disconnect (immediately when already disconnected). @param {() => void} cleanup */
  onDispose(cleanup) {
    return (this.lifetime ?? DEAD).add(cleanup)
  }

  /**
   * Dispatch `<identifier>:<name>` (bubbling) from the controller element.
   * Details carry ids, counters and enum values only.
   * @param {string} name past tense, kebab-case
   * @param {Record<string, unknown>} [detail]
   * @param {{ cancelable?: boolean, bubbles?: boolean }} [options]
   */
  emit(name, detail, options) {
    return emit(this.element, this.identifier, name, detail, options)
  }
}
